import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../utils/api';

// Social sign-in buttons — SCAFFOLDED, gated on client IDs.
//
// Renders NOTHING until a client ID is configured, so there is no UI change until
// you switch it on:
//   REACT_APP_GOOGLE_CLIENT_ID  — Google OAuth Web client ID
//   REACT_APP_APPLE_CLIENT_ID   — Apple Services ID
// On success it posts the provider token to the (gated) backend routes and hands the
// returned session to AuthContext.loginWithToken. Pair with GOOGLE_CLIENT_ID /
// APPLE_CLIENT_ID on the backend.

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || '';

function loadScript(src, id) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const s = document.createElement('script');
    s.src = src; s.id = id; s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load ' + src));
    document.head.appendChild(s);
  });
}

export default function SocialAuthButtons({ onError }) {
  const { VB } = useTheme();
  const { loginWithToken } = useAuth();
  const googleBtnRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleGoogleCredential = useCallback(async (response) => {
    if (!response || !response.credential) return;
    setBusy(true);
    try {
      const r = await api.post('/auth/google', { credential: response.credential });
      loginWithToken(r.data.token, r.data.user);
    } catch (err) {
      if (onError) onError(err?.response?.data?.message || 'Google sign-in failed. Please try again.');
    } finally { setBusy(false); }
  }, [loginWithToken, onError]);

  // Google Identity Services — render the official button.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;
    let cancelled = false;
    loadScript('https://accounts.google.com/gsi/client', 'gsi-script')
      .then(() => {
        if (cancelled || !window.google || !googleBtnRef.current) return;
        window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleGoogleCredential });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'filled_black', size: 'large', width: 320, text: 'continue_with', shape: 'pill',
        });
      })
      .catch(() => { if (onError) onError('Could not load Google sign-in.'); });
    return () => { cancelled = true; };
  }, [handleGoogleCredential, onError]);

  const handleApple = useCallback(async () => {
    if (!APPLE_CLIENT_ID) return;
    setBusy(true);
    try {
      await loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js', 'appleid-script');
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: window.location.origin + '/auth',
        usePopup: true,
      });
      const res = await window.AppleID.auth.signIn();
      const identityToken = res && res.authorization && res.authorization.id_token;
      const name = res && res.user && res.user.name
        ? [res.user.name.firstName, res.user.name.lastName].filter(Boolean).join(' ')
        : undefined;
      if (!identityToken) throw new Error('No identity token');
      const r = await api.post('/auth/apple', { identityToken, name });
      loginWithToken(r.data.token, r.data.user);
    } catch (err) {
      // Apple throws when the user closes the popup — only surface real failures.
      if (err && err.error !== 'popup_closed_by_user' && onError) {
        onError(err?.response?.data?.message || 'Apple sign-in failed. Please try again.');
      }
    } finally { setBusy(false); }
  }, [loginWithToken, onError]);

  if (!GOOGLE_CLIENT_ID && !APPLE_CLIENT_ID) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      {GOOGLE_CLIENT_ID && (
        <div ref={googleBtnRef} style={{ display: 'flex', justifyContent: 'center', minHeight: 44, marginBottom: APPLE_CLIENT_ID ? 10 : 0, opacity: busy ? 0.6 : 1 }} />
      )}
      {APPLE_CLIENT_ID && (
        <button type="button" onClick={handleApple} disabled={busy} aria-label="Continue with Apple"
          style={{ width: '100%', height: 44, borderRadius: 999, border: '1px solid rgba(255,255,255,0.16)', background: '#000', color: '#fff', fontWeight: 600, fontSize: 15, cursor: busy ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 12.04c-.03-2.6 2.13-3.85 2.22-3.91-1.21-1.77-3.1-2.02-3.77-2.05-1.6-.16-3.13.94-3.94.94-.81 0-2.07-.92-3.4-.9-1.75.03-3.37 1.02-4.27 2.58-1.82 3.16-.47 7.83 1.3 10.4.87 1.25 1.9 2.66 3.26 2.61 1.31-.05 1.8-.85 3.39-.85 1.58 0 2.03.85 3.41.82 1.41-.02 2.3-1.28 3.16-2.54.99-1.45 1.4-2.86 1.42-2.93-.03-.01-2.72-1.04-2.75-4.12M14.53 4.5c.72-.88 1.21-2.1 1.08-3.32-1.04.04-2.3.69-3.05 1.56-.67.78-1.26 2.02-1.1 3.21 1.16.09 2.35-.59 3.07-1.45"/></svg>
          Continue with Apple
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0 4px' }}>
        <div style={{ flex: 1, height: 1, background: VB.borderDark }} />
        <span style={{ fontSize: 12, color: VB.snowMuted, fontWeight: 600 }}>or</span>
        <div style={{ flex: 1, height: 1, background: VB.borderDark }} />
      </div>
    </div>
  );
}
